import { computeEmaSeries, computeMacdSeries, computeObvSeries, computeRsiSeries, computeSmaSeries } from "./trend.js";
import { predictBoostLogOdds } from "./boostModel.js";
import { predictMacroLogOdds } from "./boostModelMacro.js";
var TradingSimulator;
((TradingSimulator2) => {
  const lerp = (a, b, t) => a + (b - a) * t;
  const lastCrossing = (a, b) => {
    for (let i = a.length - 1; i >= 1; i--) {
      const a0 = a[i - 1];
      const a1 = a[i];
      const b0 = b[i - 1];
      const b1 = b[i];
      if (a0 == null || a1 == null || b0 == null || b1 == null) continue;
      const d0 = a0 - b0;
      const d1 = a1 - b1;
      if (d0 === 0) return i - 1;
      if (d0 * d1 < 0) return lerp(i - 1, i, Math.abs(d0) / (Math.abs(d0) + Math.abs(d1)));
    }
    return null;
  };
  const catmullRomDense = (xs) => {
    const n = xs.length;
    if (n < 2) return [...xs];
    const out = [];
    const at = (i) => xs[Math.max(0, Math.min(n - 1, i))];
    for (let i = 0; i < n - 1; i++) {
      const p0 = at(i - 1);
      const p1 = at(i);
      const p2 = at(i + 1);
      const p3 = at(i + 2);
      out.push(p1);
      const t = 0.5;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push(0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3));
    }
    out.push(xs[n - 1]);
    return out;
  };
  const standardize = (xs) => {
    const n = xs.length;
    if (!n) return [];
    const mean = xs.reduce((s, v) => s + v, 0) / n;
    const vari = xs.reduce((s, v) => s + (v - mean) * (v - mean), 0) / n;
    const sd = Math.sqrt(vari);
    if (!(sd > 1e-12)) return xs.map(() => 0);
    return xs.map((v) => (v - mean) / sd);
  };
  const firstPrincipalAxis = (features) => {
    const k = features.length;
    const d = features[0]?.length ?? 0;
    if (!k || !d) return [];
    const cols = [];
    for (let j = 0; j < d; j++) cols.push(standardize(features.map((f) => f[j] ?? 0)));
    const cov = [];
    for (let a = 0; a < d; a++) {
      cov.push([]);
      for (let b = 0; b < d; b++) {
        let s = 0;
        for (let i = 0; i < k; i++) s += cols[a][i] * cols[b][i];
        cov[a].push(s / Math.max(1, k - 1));
      }
    }
    let v = new Array(d).fill(1 / Math.sqrt(d));
    for (let it = 0; it < 100; it++) {
      const w = cov.map((row) => row.reduce((s, c, j) => s + c * v[j], 0));
      const norm = Math.sqrt(w.reduce((s, x) => s + x * x, 0));
      if (!(norm > 1e-12)) break;
      v = w.map((x) => x / norm);
    }
    return v;
  };
  TradingSimulator2.estimateMarketRate = (candles, indicators) => {
    const n = candles.length;
    if (n < 5) return 0.5;
    const closes = candles.map((c) => c.close);
    const maL = computeSmaSeries(closes, Math.max(2, Math.round(indicators?.maLong ?? 60)));
    const last = n - 1;
    const span = Math.min(20, n - 1);
    const base = maL[last - span];
    const slope = base != null && base !== 0 && maL[last] != null ? (maL[last] - base) / Math.abs(base) / (span / 20) : 0;
    const rets = [];
    for (let i = Math.max(1, n - 60); i < n; i++) {
      if (closes[i - 1] > 0) rets.push((closes[i] - closes[i - 1]) / closes[i - 1]);
    }
    const mean = rets.reduce((s, v) => s + v, 0) / Math.max(1, rets.length);
    const vol = Math.sqrt(rets.reduce((s, v) => s + (v - mean) * (v - mean), 0) / Math.max(1, rets.length));
    const rsi = computeRsiSeries(closes, indicators?.rsiPeriod ?? 14)[last] ?? 50;
    const score = 0.5 + Math.max(-0.35, Math.min(0.35, slope * 6)) - Math.min(0.15, vol * 3) + (rsi - 50) / 500;
    return Math.max(0, Math.min(1, score));
  };
  const ewmaAll = (xs, alpha = 0.12) => {
    let m = 0;
    let init = false;
    for (const x of xs) {
      if (!Number.isFinite(x)) continue;
      m = !init ? x : m + alpha * (x - m);
      init = true;
    }
    return init ? m : 0;
  };
  const instabilityOf = (highs, lows, closes) => {
    const n = closes.length;
    const inst = [];
    for (let i = 0; i < n; i++) {
      const c = closes[i] > 0 ? closes[i] : 0;
      inst.push(c > 0 ? Math.max(0, (highs[i] - lows[i]) / c) : 0);
    }
    const fast = ewmaAll(inst, 0.2);
    const slow = ewmaAll(inst, 0.05);
    return { level: fast, improve: slow > 1e-9 ? (slow - fast) / slow : 0 };
  };
  const DIRP_M = 10;
  TradingSimulator2.forecast = (candles, maSize, market) => {
    const m = Math.max(1, Math.floor(maSize));
    const n = candles.length;
    if (!n) return [];
    const closes = candles.map((c) => c.close);
    const base = closes[n - 1];
    if (!(base > 0)) return [];
    const rates = [];
    for (let i = n - 1; i >= Math.max(1, n - m); i--) {
      const prev = closes[i - 1];
      rates.push(prev > 0 ? (closes[i] - prev) / prev : 0);
    }
    if (!rates.length) return [];
    const edge = Math.abs((0, TradingSimulator2.directionProbability)(candles, market) - 0.5) * 2;
    const DAMP = 0.15 + 0.5 * edge;
    const out = [];
    let acc = base;
    for (const r of rates) {
      acc = Math.max(0, acc * (1 - r));
      out.push(Math.round((base + (acc - base) * DAMP) * 100) / 100);
    }
    return out;
  };
  TradingSimulator2.directionProbability = (candles, market) => {
    const n = candles.length;
    if (n < 30) return 0.5;
    const closes = candles.map((c) => c.close);
    const vols = candles.map((c) => Math.max(0, c.volume || 0));
    const i = n - 1;
    if (!(closes[i] > 0)) return 0.5;
    const rets = [];
    for (let j = i - DIRP_M + 1; j <= i; j++) {
      const prev = closes[j - 1];
      if (!(prev > 0)) return 0.5;
      rets.push((closes[j] - prev) / prev);
    }
    const sumM = rets.reduce((a, b) => a + b, 0);
    const mom3 = rets.slice(-3).reduce((a, b) => a + b, 0);
    const mean = sumM / rets.length;
    const vol = Math.sqrt(rets.reduce((a, r) => a + (r - mean) * (r - mean), 0) / rets.length);
    let up = 0;
    let dn = 0;
    for (const r of rets) {
      if (r > 0) up += r;
      else dn -= r;
    }
    const rsi = up + dn > 0 ? up / (up + dn) : 0.5;
    const v0 = vols.slice(i - DIRP_M + 1, i + 1).reduce((a, b) => a + b, 0) / DIRP_M;
    const vP = vols.slice(i - 2 * DIRP_M + 1, i - DIRP_M + 1).reduce((a, b) => a + b, 0) / DIRP_M;
    const vtrend = vP > 0 ? (v0 - vP) / vP : 0;
    const macd = computeMacdSeries(closes, 12, 26, 9);
    const md = macd.macd[i] != null && macd.signal[i] != null ? (macd.macd[i] - macd.signal[i]) / closes[i] * 100 : 0;
    const obv = computeObvSeries(closes, vols);
    const ob = obv[i - 10] ? (obv[i] - obv[i - 5]) / Math.abs(obv[i - 10]) * 100 : 0;
    const vWin = vols.slice(i - 20, i);
    const vAvg = vWin.reduce((a, b) => a + b, 0) / Math.max(1, vWin.length);
    const vr = vAvg > 0 ? vols[i] / vAvg : 1;
    const ma30 = closes.slice(i - 29, i + 1).reduce((a, b) => a + b, 0) / 30;
    const w20 = closes.slice(i - 19, i + 1);
    const wMin = Math.min(...w20);
    const leftA = w20.slice(0, 10).reduce((s, x) => s + (x - wMin), 0) / closes[i];
    const rightA = w20.slice(10).reduce((s, x) => s + (x - wMin), 0) / closes[i];
    const rightRatio = rightA / Math.max(leftA + rightA, 1e-12);
    const f = [sumM, mom3, vol, rsi - 0.5, vtrend, md, ob, vr, (closes[i] - ma30) / closes[i], rightRatio];
    if (f.some((v) => !Number.isFinite(v))) return 0.5;
    if (market && market.tnx.length === n && market.wti.length === n && market.spx.length === n) {
      const chg = (xs) => {
        const a = xs[i - 5];
        const b = xs[i];
        return a > 0 && Number.isFinite(a) && Number.isFinite(b) ? (b - a) / a : null;
      };
      const t = chg(market.tnx);
      const w = chg(market.wti);
      const s = chg(market.spx);
      if (t != null && w != null && s != null) {
        return 1 / (1 + Math.exp(-predictMacroLogOdds([...f, t, w, s])));
      }
    }
    return 1 / (1 + Math.exp(-predictBoostLogOdds(f)));
  };
  TradingSimulator2.findBestConfig = (candles, options) => {
    const n = candles.length;
    const sRate = options?.strategyRate ?? 0.5;
    const mRate = options?.marketRate ?? 0.5;
    const ind = options?.indicators;
    const applyMode = mRate < 0.35 ? "min" : mRate > 0.65 ? "max" : "combined";
    const tn = options?.tuning ?? {};
    const execPolicy = tn.maxConsecBars != null && tn.maxConsecBars <= 0 ? void 0 : {
      maxConsecBars: Math.max(1, Math.floor(tn.maxConsecBars ?? 2)),
      restBars: Math.max(1, Math.floor(tn.consecRestBars ?? 3)),
      minBudgetEquityPct: tn.minBudgetEquityPct ?? 0.01,
      ...tn.batchBars == null ? { batchBars: 2 } : tn.batchBars >= 2 ? { batchBars: Math.floor(tn.batchBars) } : {}
    };
    if (n < 3) return { conditions: [], applyMode, conviction: 0, upProbability: 0.5, execPolicy };
    const closes = candles.map((c) => c.close);
    const volumes = candles.map((c) => Math.max(0, c.volume || 0));
    const upProbability = (0, TradingSimulator2.directionProbability)(candles, options?.macro);
    const dirEdge = Math.abs(upProbability - 0.5) * 2;
    const maFn = ind?.maExponential ? computeEmaSeries : computeSmaSeries;
    const pS = Math.max(2, Math.round(ind?.maShort ?? 5));
    const pM = Math.max(2, Math.round(ind?.maMid ?? 10));
    const pL = Math.max(2, Math.round(ind?.maLong ?? 60));
    const maS = maFn(closes, pS);
    const maM = maFn(closes, pM);
    const maL = maFn(closes, pL);
    const macd = computeMacdSeries(closes, ind?.macdFast ?? 12, ind?.macdSlow ?? 26, ind?.macdSignal ?? 9);
    const rsi = computeRsiSeries(closes, ind?.rsiPeriod ?? 14);
    const rsiOb = ind?.rsiOb ?? 70;
    const rsiOs = ind?.rsiOs ?? 30;
    const dense = catmullRomDense(closes);
    const dv = [];
    for (let i = 1; i < dense.length; i++) dv.push(dense[i - 1] > 0 ? (dense[i] - dense[i - 1]) / dense[i - 1] : 0);
    const vel = [0];
    for (let i = 1; i < n; i++) vel.push(((dv[(i - 1) * 2] ?? 0) + (dv[(i - 1) * 2 + 1] ?? 0)) / 2);
    const acc = [0];
    for (let i = 1; i < n; i++) acc.push(vel[i] - vel[i - 1]);
    const massROC = [0];
    for (let i = 1; i < n; i++) massROC.push(volumes[i - 1] > 0 ? (volumes[i] - volumes[i - 1]) / volumes[i - 1] : 0);
    const rawForce = volumes.map((m, i) => m * acc[i]);
    const sortedAbs = rawForce.map(Math.abs).sort((a, b) => a - b);
    const medForce = sortedAbs[Math.floor(sortedAbs.length / 2)] || 1;
    const force = rawForce.map((f) => f / medForce);
    const gravity = closes.map((c, i) => maL[i] != null && c > 0 ? (maL[i] - c) / c : 0);
    const K = Math.min(n, Math.max(pM, 5));
    const integral = vel.slice(n - K).reduce((s, v) => s + v, 0);
    const recent = vel.slice(n - K);
    const meanV = recent.reduce((s, v) => s + v, 0) / Math.max(1, recent.length);
    const oscillation = Math.sqrt(recent.reduce((s, v) => s + (v - meanV) * (v - meanV), 0) / Math.max(1, recent.length));
    const crossMM = lastCrossing(maS, maM);
    const settling = crossMM == null ? K : Math.max(0, n - 1 - crossMM);
    const crossL = lastCrossing(closes.map((c) => c), maL);
    let overshoot = 0;
    if (crossL != null) {
      for (let i = Math.max(0, Math.floor(crossL)); i < n; i++) {
        if (maL[i] != null && maL[i] !== 0) overshoot = Math.max(overshoot, Math.abs((closes[i] - maL[i]) / maL[i]));
      }
    }
    const feats = [];
    for (let i = n - K; i < n; i++) feats.push([vel[i], acc[i], force[i], gravity[i], massROC[i]]);
    const axis = firstPrincipalAxis(feats);
    const lastStd = axis.map((_, j) => {
      const col = feats.map((f) => f[j]);
      const s = standardize(col);
      return s[s.length - 1] ?? 0;
    });
    const regime = axis.reduce((s, a, j) => s + a * lastStd[j], 0);
    const last = n - 1;
    const alignG = Math.sign(vel[last]) === Math.sign(gravity[last]) && vel[last] !== 0 ? 1 : -0.5;
    const baseConviction = 1 / (1 + Math.exp(-(regime * 0.8 + Math.tanh(force[last] * 0.5) * 0.6 + alignG * 0.4 + Math.tanh(integral * 20) * 0.5)));
    const blend = Math.max(0, Math.min(1, tn.convictionBlend ?? 0.5));
    const conviction = (1 - blend) * baseConviction + blend * (0.5 + dirEdge * 0.5);
    const marketScale = 0.5 + mRate;
    const momUp = Math.tanh(Math.max(0, vel[last]) * 50);
    const momDn = Math.tanh(Math.max(0, -vel[last]) * 50);
    const buySkew = 0.7 + 0.6 * mRate;
    const sellSkew = 1.3 - 0.6 * mRate;
    const conservative = mRate < 0.35;
    const aggressive = mRate > 0.65;
    const percentCap = Math.max(5, Math.round((conservative ? 26 : aggressive ? 65 : 46) * (tn.percentCapScale ?? 1)));
    const sizeOf = (base, side) => {
      const bias = side === "up-buy" ? 0.7 + 0.6 * sRate : side === "down-buy" ? 1.3 - 0.6 * sRate : 0.9 + 0.2 * sRate;
      const mom = side === "up-buy" ? 1 + momUp * (0.4 + 0.6 * sRate) : side === "down-buy" ? 1 + momDn * (0.6 - 0.4 * sRate) : 1 + momDn * (0.4 + 0.4 * sRate);
      const skew = side === "sell" ? sellSkew : buySkew;
      return Math.max(5, Math.min(percentCap, Math.round(base * (0.6 + conviction * 0.8) * marketScale * bias * mom * skew)));
    };
    const rawCd = (3 + settling / 2 + oscillation * 200) * (1.6 - 0.6 * mRate);
    const cooldown = tn.cooldownScale == null ? Math.max(3, Math.min(14, Math.round(rawCd))) : Math.max(1, Math.min(30, Math.round(rawCd * tn.cooldownScale)));
    const quietBars = Math.max(0, (tn.quietBarsAdd == null ? 4 : 0) + (oscillation > 0.02 ? 1 : 0) + (conservative ? 1 : 0) + Math.round(tn.quietBarsAdd ?? 0));
    const clarity = 1 / (1 + Math.exp(-Math.abs(regime) * 1.2));
    const gate = 0.5 - mRate * 0.2 + (tn.gateShift ?? 0);
    const minEdge = Math.max(0, Math.min(1, tn.minEdge ?? 0.2));
    if (clarity < gate || n >= 30 && dirEdge < minEdge) return { conditions: [], applyMode, conviction, upProbability, execPolicy };
    const conditions = [];
    const lastClose = closes[last];
    const oversoldDepth = rsi[last] <= rsiOs ? Math.min(1, (rsiOs - rsi[last]) / 20) : 0;
    const belowLong = maL[last] != null && maL[last] > 0 && lastClose < maL[last] ? Math.min(1, (maL[last] - lastClose) / maL[last] / 0.1) : 0;
    const dipScore = Math.max(0, Math.min(1, 0.35 * oversoldDepth + 0.35 * momDn + 0.3 * belowLong));
    const downAction = dipScore > sRate ? "buy" : "sell";
    const sS = maS[last];
    const sM = maM[last];
    if (sS != null && sM != null && sS !== sM) {
      const up = sS > sM;
      const action = up ? "buy" : downAction;
      {
        conditions.push({
          source: "MA",
          left: "\uB2E8\uAE30MA",
          right: "\uC911\uAE30MA",
          operator: up ? ">" : "<",
          action,
          percent: sizeOf(20, up ? "up-buy" : downAction === "buy" ? "down-buy" : "sell"),
          description: up ? "\uB2E8\uAE30MA\uAC00 \uC911\uAE30MA \uC0C1\uD68C" : downAction === "buy" ? "\uB2E8\uAE30MA\uAC00 \uC911\uAE30MA \uD558\uD68C (\uB20C\uB9BC \uB9E4\uC218)" : "\uB2E8\uAE30MA\uAC00 \uC911\uAE30MA \uD558\uD68C",
          cooldownBars: cooldown,
          noTradeBars: quietBars
        });
      }
    }
    const mV = macd.macd[last];
    const gV = macd.signal[last];
    if (Number.isFinite(mV) && Number.isFinite(gV) && mV !== gV) {
      const up = mV > gV;
      const action = up ? "buy" : downAction;
      {
        conditions.push({
          source: "MACD",
          left: "MACD",
          right: "SIGNAL",
          operator: up ? ">" : "<",
          action,
          percent: sizeOf(20, up ? "up-buy" : downAction === "buy" ? "down-buy" : "sell"),
          description: up ? "MACD SIGNAL \uACE8\uB4E0\uD06C\uB85C\uC2A4" : downAction === "buy" ? "MACD SIGNAL \uB370\uB4DC\uD06C\uB85C\uC2A4 (\uB20C\uB9BC \uB9E4\uC218)" : "MACD SIGNAL \uB370\uB4DC\uD06C\uB85C\uC2A4",
          cooldownBars: cooldown,
          noTradeBars: quietBars
        });
      }
    }
    const rV = rsi[last];
    if (Number.isFinite(rV)) {
      if (rV >= rsiOb) {
        conditions.push({
          source: "RSI",
          left: "RSI",
          right: "\uACFC\uB9E4\uC218",
          operator: ">=",
          action: "sell",
          percent: sizeOf(10 + Math.round(overshoot * 500), "sell"),
          description: "RSI \uACFC\uB9E4\uC218",
          cooldownBars: cooldown + 2,
          noTradeBars: quietBars
        });
      } else if (rV <= rsiOs) {
        {
          conditions.push({
            source: "RSI",
            left: "RSI",
            right: "\uACFC\uB9E4\uB3C4",
            operator: "<=",
            action: downAction,
            percent: sizeOf(10, downAction === "buy" ? "down-buy" : "sell"),
            description: downAction === "buy" ? "RSI \uACFC\uB9E4\uB3C4 (\uB20C\uB9BC \uB9E4\uC218)" : "RSI \uACFC\uB9E4\uB3C4",
            cooldownBars: cooldown + 2,
            noTradeBars: quietBars
          });
        }
      }
    }
    if (options?.invertActions) {
      for (const c of conditions) c.action = c.action === "buy" ? "sell" : "buy";
    }
    return { conditions, applyMode, conviction, upProbability, execPolicy };
  };
  TradingSimulator2.simulate = (options) => {
    const candles = options.candles ?? [];
    const conditions = options.config?.conditions ?? [];
    const applyMode = options.config?.applyMode ?? "combined";
    const ind = options.indicators;
    const fee = options.fee ?? 0;
    const closes = candles.map((c) => c.close);
    const maFn = ind?.maExponential ? computeEmaSeries : computeSmaSeries;
    const maS = maFn(closes, ind?.maShort ?? 5);
    const maM = maFn(closes, ind?.maMid ?? 10);
    const maL = maFn(closes, ind?.maLong ?? 60);
    const macd = computeMacdSeries(closes, ind?.macdFast ?? 12, ind?.macdSlow ?? 26, ind?.macdSignal ?? 9);
    const rsi = computeRsiSeries(closes, ind?.rsiPeriod ?? 14);
    const rsiOb = ind?.rsiOb ?? 70;
    const rsiOs = ind?.rsiOs ?? 30;
    const operandOf = (name, i) => {
      switch (name) {
        case "\uB2E8\uAE30MA":
          return maS[i] ?? null;
        case "\uC911\uAE30MA":
          return maM[i] ?? null;
        case "\uC7A5\uAE30MA":
          return maL[i] ?? null;
        case "MACD":
          return macd.macd[i] ?? null;
        case "SIGNAL":
          return macd.signal[i] ?? null;
        case "RSI":
          return rsi[i] ?? null;
        case "\uACFC\uB9E4\uC218":
          return rsiOb;
        case "\uACFC\uB9E4\uB3C4":
          return rsiOs;
        default: {
          const n = Number(name);
          return Number.isFinite(n) ? n : null;
        }
      }
    };
    const compare = (l, op, r) => {
      switch (op) {
        case ">":
          return l > r;
        case ">=":
          return l >= r;
        case "<":
          return l < r;
        case "<=":
          return l <= r;
        case "==":
          return l === r;
        case "!=":
          return l !== r;
      }
    };
    let cash = options.capital ?? 0;
    let shares = 0;
    let avgPrice = 0;
    const histBars = [];
    for (const h of options.history ?? []) {
      if (h.initial) {
        const q = Math.max(0, Math.floor(h.shares) || 0);
        if (q > 0) {
          avgPrice = shares + q > 0 ? (avgPrice * shares + h.price * q) / (shares + q) : h.price;
          shares += q;
        }
        continue;
      }
      if (h.action === "buy") {
        const affordable = Math.min(h.shares, Math.floor(cash / (h.price * (1 + fee))));
        if (affordable > 0) {
          const cost = affordable * h.price * (1 + fee);
          avgPrice = (avgPrice * shares + cost) / (shares + affordable);
          cash -= cost;
          shares += affordable;
          const bi = candles.findIndex((c) => c.date === h.date);
          if (bi >= 0) histBars.push(bi);
        }
      } else if (h.action === "sell") {
        const s = Math.min(h.shares, shares);
        cash += s * h.price * (1 - fee);
        shares -= s;
        if (shares <= 0) avgPrice = 0;
        if (s > 0) {
          const bi = candles.findIndex((c) => c.date === h.date);
          if (bi >= 0) histBars.push(bi);
        }
      }
    }
    const trades = [];
    const barredUntil = new Array(conditions.length).fill(-1);
    let lastTradeBar = histBars.length ? Math.max(...histBars) : -Infinity;
    const execPolicy = options.config?.execPolicy;
    const batchN = execPolicy?.batchBars != null && execPolicy.batchBars >= 2 ? Math.floor(execPolicy.batchBars) : 0;
    const bs0 = options.batchState;
    let pendBuy = bs0?.buy ?? 0;
    let pendSell = bs0?.sell ?? 0;
    let pendBuyCond = bs0?.buyCond ? { ...bs0.buyCond } : void 0;
    let pendSellCond = bs0?.sellCond ? { ...bs0.sellCond } : void 0;
    let consecExecBars = 0;
    let restUntil = -1;
    const noteBar = (t0, bar) => {
      if (!execPolicy) return;
      const n = trades.slice(t0).filter((t) => t.action === "buy" || t.action === "sell").length;
      if (n === 0) {
        consecExecBars = 0;
        return;
      }
      if (bar > restUntil) {
        consecExecBars += 1;
        if (execPolicy.maxConsecBars >= 1 && consecExecBars >= execPolicy.maxConsecBars) {
          restUntil = bar + execPolicy.restBars;
          consecExecBars = 0;
        }
      }
    };
    const doExecute = (exCond, exCandidates, c, i, price) => {
      if (exCond.action === "buy") {
        const budget = cash * (exCond.percent / 100);
        const minPct = execPolicy?.minBudgetEquityPct ?? 0.01;
        if (minPct > 0 && budget < minPct * (cash + shares * price)) {
          trades.push({ date: c.date, action: "buy-failed", price, shares: 0, reason: "\uC18C\uC561\uC81C\uC678", condition: { ...exCond }, candidates: exCandidates });
          return;
        }
        const qty = Math.floor(budget / (price * (1 + fee)));
        if (qty < 1) {
          trades.push({ date: c.date, action: "buy-failed", price, shares: 0, reason: "\uC794\uC561\uBD80\uC871", condition: { ...exCond }, candidates: exCandidates });
        } else {
          const cost = qty * price * (1 + fee);
          avgPrice = (avgPrice * shares + cost) / (shares + qty);
          cash -= cost;
          shares += qty;
          trades.push({ date: c.date, action: "buy", price, shares: qty, condition: { ...exCond }, candidates: exCandidates });
          lastTradeBar = i;
        }
      } else {
        const qty = Math.floor(shares * (exCond.percent / 100));
        if (qty < 1) {
          trades.push({ date: c.date, action: "sell-failed", price, shares: 0, reason: "\uBCF4\uC720\uC5C6\uC74C", condition: { ...exCond }, candidates: exCandidates });
        } else {
          cash += qty * price * (1 - fee);
          shares -= qty;
          if (shares <= 0) avgPrice = 0;
          trades.push({ date: c.date, action: "sell", price, shares: qty, condition: { ...exCond }, candidates: exCandidates });
          lastTradeBar = i;
        }
      }
    };
    const flushBatch = (c, i, price) => {
      const net = pendBuy - pendSell;
      const dom = net > 0 ? pendBuyCond : net < 0 ? pendSellCond : void 0;
      pendBuy = 0;
      pendSell = 0;
      pendBuyCond = void 0;
      pendSellCond = void 0;
      if (net === 0 || !dom) return;
      doExecute({ ...dom, percent: Math.min(50, Math.abs(net)) }, conditions.map((x) => ({ ...x })), c, i, price);
    };
    candles.forEach((c, i) => {
      const t0 = trades.length;
      const price = c.close;
      if (!(price > 0)) {
        noteBar(t0, i);
        return;
      }
      if (i < (options.fromBar ?? 0)) return;
      const stopPct = options.risk?.stopLossPct;
      const takePct = options.risk?.takeProfitPct;
      if (shares > 0 && avgPrice > 0 && (stopPct != null || takePct != null)) {
        const ret = (price - avgPrice) / avgPrice * 100;
        if (stopPct != null && ret <= -Math.abs(stopPct)) {
          const candidates = conditions.map((x) => ({ ...x }));
          cash += shares * price * (1 - fee);
          trades.push({ date: c.date, action: "sell", price, shares, reason: "\uC2A4\uD0D1\uB85C\uC2A4", candidates });
          shares = 0;
          avgPrice = 0;
          lastTradeBar = i;
          pendBuy = 0;
          pendSell = 0;
          pendBuyCond = void 0;
          pendSellCond = void 0;
          noteBar(t0, i);
          return;
        }
        if (takePct != null && ret >= Math.abs(takePct)) {
          const q = Math.floor(shares / 2);
          if (q > 0) {
            const candidates = conditions.map((x) => ({ ...x }));
            cash += q * price * (1 - fee);
            shares -= q;
            if (shares <= 0) avgPrice = 0;
            trades.push({ date: c.date, action: "sell", price, shares: q, reason: "\uC775\uC808", candidates });
            lastTradeBar = i;
          }
        }
      }
      if (execPolicy && i <= restUntil) {
        noteBar(t0, i);
        return;
      }
      const fired = [];
      conditions.forEach((cond, ci) => {
        if (i <= barredUntil[ci]) return;
        if (cond.noTradeBars != null && i - lastTradeBar <= cond.noTradeBars) return;
        const l = operandOf(cond.left, i);
        const r = operandOf(cond.right, i);
        if (l == null || r == null) return;
        if (compare(l, cond.operator, r)) fired.push(ci);
      });
      if (!fired.length) {
        noteBar(t0, i);
        return;
      }
      let pick = null;
      let execPercent = 0;
      if (applyMode === "max") {
        const m = Math.max(...fired.map((ci) => conditions[ci].percent));
        pick = fired.find((ci) => conditions[ci].percent === m) ?? null;
        execPercent = m;
      } else if (applyMode === "min") {
        const m = Math.min(...fired.map((ci) => conditions[ci].percent));
        pick = fired.find((ci) => conditions[ci].percent === m) ?? null;
        execPercent = m;
      } else {
        const sumOf = (side2) => fired.filter((ci) => conditions[ci].action === side2).reduce((s, ci) => s + conditions[ci].percent, 0);
        const side = sumOf("buy") >= sumOf("sell") ? "buy" : "sell";
        const cands = fired.filter((ci) => conditions[ci].action === side);
        const m = Math.max(...cands.map((ci) => conditions[ci].percent));
        pick = cands.find((ci) => conditions[ci].percent === m) ?? null;
        execPercent = Math.min(50, side === "buy" ? sumOf("buy") : sumOf("sell"));
      }
      if (pick == null) {
        noteBar(t0, i);
        return;
      }
      {
        const ci = pick;
        const cond = { ...conditions[ci], percent: execPercent, applyMode };
        const candidates = conditions.map((c2) => ({ ...c2 }));
        if (batchN >= 2) {
          if (cond.action === "buy") {
            pendBuy += execPercent;
            if (!pendBuyCond || execPercent > pendBuyCond.percent) pendBuyCond = { ...cond };
          } else {
            pendSell += execPercent;
            if (!pendSellCond || execPercent > pendSellCond.percent) pendSellCond = { ...cond };
          }
          if (cond.cooldownBars != null) barredUntil[ci] = i + cond.cooldownBars;
          if (i % batchN === batchN - 1) flushBatch(c, i, price);
        } else {
          doExecute(cond, candidates, c, i, price);
          if (cond.cooldownBars != null) barredUntil[ci] = i + cond.cooldownBars;
        }
      }
      noteBar(t0, i);
    });
    return {
      trades,
      batchState: {
        buy: pendBuy,
        sell: pendSell,
        ...pendBuyCond ? { buyCond: { ...pendBuyCond } } : {},
        ...pendSellCond ? { sellCond: { ...pendSellCond } } : {}
      },
      returnPct: (() => {
        if (!candles.length) return 0;
        const first = candles[0].close;
        const lastC = candles[candles.length - 1].close;
        const initValue = (options.history ?? []).filter((h) => h.initial).reduce((s, h) => s + Math.max(0, Math.floor(h.shares) || 0) * first, 0);
        const startEq = (options.capital ?? 0) + initValue;
        if (!(startEq > 0)) return 0;
        return (cash + shares * lastC - startEq) / startEq * 100;
      })()
    };
  };
})(TradingSimulator || (TradingSimulator = {}));
export {
  TradingSimulator
};
//# sourceMappingURL=TradingSimulator.js.map
